import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
	WalletOperationResolutionAction,
	WalletOperationResolved,
	resolveWalletOperation,
} from 'lib/models/walletOperations';
import { enqueueSnackbarError, enqueueSnackbarSuccess } from 'lib/helpers';

import useWalletOperationLabels from './useWalletOperationLabels';

/**
 * The three manual actions, with what the API answers to each of them.
 *
 * A refused action is not a failure of the screen: every refusal the API defines is a
 * different sentence and a different thing for the operator to do next, so it is toasted as
 * itself rather than as "no se pudo". A refusal also leaves the record exactly where it
 * was, which is why nothing is returned for the caller to re-render from.
 */
function useWalletOperationActions() {
	const { t } = useTranslation();
	const { refusalMessage, resolutionResultLabel } = useWalletOperationLabels();

	const [loading, setLoading] = useState(false);

	/**
	 * Sends one action, and says what came of it.
	 *
	 * @param id The operation being resolved
	 * @param action What is being done to it
	 * @param reason Why, as the operator stated it
	 *
	 * @returns The entry that was recorded and the operation it was recorded on, or null if
	 *   the API refused
	 */
	const resolve = useCallback(
		async (
			id: string,
			action: WalletOperationResolutionAction,
			reason: string
		): Promise<WalletOperationResolved | null> => {
			setLoading(true);
			const response = await resolveWalletOperation(id, action, reason);
			setLoading(false);

			if (!response.ok) {
				enqueueSnackbarError(refusalMessage(response.code, response.message));
				return null;
			}

			enqueueSnackbarSuccess(
				t('common:walletOperationResolved', {
					result: resolutionResultLabel(response.resolution.result),
				})
			);

			return { resolution: response.resolution, operation: response.operation };
		},
		[refusalMessage, resolutionResultLabel, t]
	);

	return { loading, resolve };
}

export default useWalletOperationActions;
